import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";

import {
  AuthApi,
  IApiError,
  isLoggedInAtom,
  isLoginInProgressAtom,
  Log,
  loginErrorsAtom,
  normalizeErrors,
  RecaptchaEnterpriseActions,
  updateTokensAndReconnectSocket,
} from "@ryde/shared";

import { useSimpleNavigation } from "../../navigation";
import { useRecaptchaToken } from "./use-recaptcha";

interface IUseOtpLoginResult {
  requestOtp: () => Promise<boolean>;
  loginOtp: (code: string) => Promise<void>;
}

export const useOtpLogin = (): IUseOtpLoginResult => {
  const { t } = useTranslation();
  const setLoginErrors = useSetRecoilState(loginErrorsAtom);
  const setLoggingInProgress = useSetRecoilState(isLoginInProgressAtom);
  const setIsLoggedIn = useSetRecoilState(isLoggedInAtom);
  const { navigateToHomeOrLoginReturnUrl } = useSimpleNavigation();
  const { getValues } = useFormContext();
  const { getRecaptchaToken } = useRecaptchaToken();

  const requestOtp = async (): Promise<boolean> => {
    const phone = getValues("Phone");

    try {
      const recaptchaToken = await getRecaptchaToken(
        RecaptchaEnterpriseActions.REQUEST_OTP,
      );

      await AuthApi.requestOtp({ phone, recaptchaToken });
      return true;
    } catch (error) {
      Log.error("Error requesting OTP", error);
      Log.errorAndToast(
        t("errors.errorRequestOTPForPhone", { phoneNumber: phone }),
      );
      return false;
    }
  };

  const loginOtp = async (code: string): Promise<void> => {
    try {
      const phone = getValues("Phone");
      setLoginErrors([]);
      setLoggingInProgress(true);

      const recaptchaToken = await getRecaptchaToken(
        RecaptchaEnterpriseActions.LOGIN,
      );

      const profile = await AuthApi.sessionLoginOtp({
        phone,
        code,
        recaptchaToken,
      });

      Log.debug("Auth session established for phone: ", phone);
      updateTokensAndReconnectSocket({
        accessToken: null,
        refreshToken: null,
      });

      if (profile) {
        setIsLoggedIn(true);
        navigateToHomeOrLoginReturnUrl();
      }
    } catch (error) {
      const errorsNormalized = normalizeErrors(error as IApiError, t);
      setLoginErrors(errorsNormalized);
      Log.warnAndToast(t("errors.authenticationError"), error);
    } finally {
      setLoggingInProgress(false);
    }
  };

  return { requestOtp, loginOtp };
};
